import { CustomerBase } from "../json-schemas/base/customer"
import { UrssafConnectBase } from "../json-schemas/base/urssaf_connect"
import { BackofficeCustomerCountPerScopeResponse } from "../json-schemas/serializers/backoffice-customer-count-per-scope-response"
import { BackofficeCustomerGenerateAttestationFiscalesForOneResponse } from "../json-schemas/serializers/backoffice-customer-generate-attestation-fiscales-for-one-response"
import { BackofficeCustomerListResponse } from "../json-schemas/serializers/backoffice-customer-list-response"
import { CustomerCheckUrssafRegistrationStatusResponse } from "../json-schemas/serializers/customer-check-urssaf-registration-status-response"
import { CustomerWriteNfcCustomerProps } from "../json-schemas/serializers/customer-write-nfc-customer-props"
import { SuppliersCustomerForCastingProps } from "../json-schemas/serializers/suppliers-customer-for-casting-props"
import { UserPaymentModeEditResponse } from "../json-schemas/serializers/user-payment-mode-edit-response"
import { httpDelete, httpGet, httpGetRoute, httpParamsRemoveDiacriticsToSortableNames, httpPatch, httpPost } from "../utils/http.service"

export interface RansackQuery {
  'q[corporate_name_null]'?: boolean
  'q[sortable_name_or_reversed_sortable_name_cont]'?: string
  'q[name_or_phone_number_or_email_or_customer_code_cont]'?: string
  'q[sortable_name_start]'?: string
  'q[market_type_id]'?: number
  'q[market_type_eq]'?: string
  'q[id_not_in][]'?: number[]
  'q[id_eq]'?: number
  scope?: typeof customerScopes[number]
  page?: number
  per_page?: number
}

export const customerScopes = ['active', 'with_casting_to_answer', 'with_casting_proposal_sent', 'with_contract_to_sign', 'with_invoice_to_send', 'inactive'] as const

export function customerShowPath (id: CustomerBase['id']) {
  return `/${httpGetRoute()}/customers/${id}`
}

export function customerPaymentFacilitiesPath (id: CustomerBase['id']) {
  return customerShowPath(id) + '/payment_facilities'
}

export function customerCreate (body: any) {
  return httpPost(`/${httpGetRoute()}/customers`, { customer: body })
}

export function customerCheckUrssafRegistrationStatus (id: CustomerBase['id'], params: { urssaf_connect_id: UrssafConnectBase['id'] }): Promise<CustomerCheckUrssafRegistrationStatusResponse> {
  const basePath = window.currentUser.type === 'Operator' ? customerShowPath(id) : '/' + httpGetRoute()
  return httpGet(`${basePath}/check_urssaf_registration_status`, params)
}

export function customerCreateBulk (bodies: any, skipedErrors: string[]) {
  return httpPost(`/${httpGetRoute()}/customers/create_bulk`, { customers: bodies, skipped_errors: skipedErrors })
}

export function customerIndex (query: RansackQuery, output: 'index_write_nfc'): Promise<CustomerWriteNfcCustomerProps>
export function customerIndex (query: RansackQuery, output: 'index_casting_form'): Promise<SuppliersCustomerForCastingProps[]>
export function customerIndex (query: RansackQuery, output: 'index_list'): Promise<BackofficeCustomerListResponse>
export function customerIndex (query: RansackQuery, output: 'index_write_nfc' | 'index_casting_form' | 'index_list') {
  return httpGet(`/${httpGetRoute()}/customers`, { output, ...httpParamsRemoveDiacriticsToSortableNames(query) })
}

export function customerCountPerScope (query: RansackQuery): Promise<BackofficeCustomerCountPerScopeResponse> {
  return httpGet(`/${httpGetRoute()}/customers/count_per_scope`, query) 
}

export function customerSignUp (body: any) {
  body.market_id ||= +window.market_id
  body.market_type ||= window.market_type
  return httpPost('/customers', { customer: body })
}

export function customerSearchFuzzyForNewCasting (body: any, output: 'index_write_nfc'): Promise<CustomerWriteNfcCustomerProps>
export function customerSearchFuzzyForNewCasting (body: any, output: 'index_casting_form'): Promise<SuppliersCustomerForCastingProps[]>
export function customerSearchFuzzyForNewCasting (body: any, output: 'index_list'): Promise<BackofficeCustomerListResponse>
export function customerSearchFuzzyForNewCasting (body: any, output: 'index_write_nfc' | 'index_casting_form' | 'index_list') {
  return httpPost(`/${httpGetRoute()}/customers/search_fuzzy_for_new_casting`, { customer: body, output })
}

export function customerGenerateAttestationFiscalesForAll () {
  return httpPost(`/${httpGetRoute()}/customers/generate_attestation_fiscales_for_all`, {})
}

export function customerGenerateAttestationFiscalesForOne (customerId: number): Promise<BackofficeCustomerGenerateAttestationFiscalesForOneResponse> {
  return httpPost(`/${httpGetRoute()}/customers/${customerId}/generate_attestation_fiscales_for_one`, {})
}

export function customerSampleAttestationFiscalesPath () {
  return `/${httpGetRoute()}/customers/sample_attestation_fiscale.pdf`
}

export function customerDelete (id: CustomerBase['id']) {
  return httpDelete(`/${httpGetRoute()}/customers/${id}`)
}

export function customerEditPaymentFacilities (id: CustomerBase['id']): Promise<UserPaymentModeEditResponse> {
  return httpGet(customerBasePath(id) + '/payment_facilities')
}

export function customerUpdate (id: CustomerBase['id'], body: any) {
  return httpPatch(customerBasePath(id), { customer: body })
}

function customerBasePath (id: CustomerBase['id']) {
  return window.currentUser.type === 'Operator' ? `/${httpGetRoute()}/customers/${id}` : `/${httpGetRoute()}`
}
